import React from "react";
import { Element, JSXFunctionSerializer } from "@prismicio/react";
import "./htmlSerializer.css";
import { Link } from "@xometry/xometry_loft";
import { buildRelativeUrl } from "./environment-urls";
import Youtube from "../components/youtube/youtube";
import { linkTo, textToHTMLAttribute } from "./contentUtils";
import GatsbyLink from "../components/link/link";
import Vimeo from "../components/vimeo/vimeo";
import HeadingAnchor from "../components/heading-anchor/heading-anchor";
import { RTEmbedNode, RTLinkNode, RTTextNodeBase } from "@prismicio/types";

// -- Function to add unique key to props
const propsWithUniqueKey = function (props: { [key: string]: any }, key: any) {
  return Object.assign(props || {}, { key });
};

const baseSerializer: JSXFunctionSerializer = function (
  type,
  element,
  content,
  children,
  key
) {
  switch (type) {
    case Element.paragraph:
      let paragraph = element as RTTextNodeBase;
      if (paragraph.text == "") {
        return React.createElement("br", propsWithUniqueKey({}, key));
      }

      return React.createElement(
        "p",
        propsWithUniqueKey({ className: "paragraph" }, key),
        children
      );
    case Element.hyperlink:
      let hyperlink = element as RTLinkNode;
      let rawURL = "";
      if (hyperlink && hyperlink.data?.link_type === "Document") {
        // linkTo is used to handle internal linking
        rawURL = linkTo(hyperlink.data);
        const url = buildRelativeUrl(rawURL);
        return (
          <GatsbyLink to={url} className={hyperlink.data?.link_type}>
            {content}
          </GatsbyLink>
        );
      } else if (
        hyperlink.data?.link_type === "Web" ||
        hyperlink.data?.link_type === "Media"
      ) {
        rawURL = hyperlink.data.url || "";
        const url = buildRelativeUrl(rawURL);
        // if this is not a xometry link then open in a new tab
        const target = url.startsWith("/") ? undefined : "_blank";
        return (
          <Link
            key={key}
            href={url}
            target={target}
            className={`${hyperlink.type} ${hyperlink.data.link_type}`}
          >
            {content}
          </Link>
        );
      }
    case Element.strong:
      return React.createElement(
        "strong",
        propsWithUniqueKey({}, key),
        children
      );
    case Element.embed:
      let embed = element as RTEmbedNode;
      if (
        embed.oembed.provider_name === "YouTube" &&
        embed.oembed.thumbnail_url &&
        embed.oembed.title
      ) {
        return (
          <Youtube
            key={key}
            embedURL={embed.oembed.embed_url}
            thumbnailURL={embed.oembed.thumbnail_url}
            title={embed.oembed.title}
          />
        );
      } else if (
        embed.oembed.provider_name === "Vimeo" &&
        embed.oembed.embed_url
      ) {
        return <Vimeo key={key} iframeHtml={embed.oembed.html} />;
      } else if (
        embed.oembed.provider_name === "static_image" &&
        embed.oembed.url
      ) {
        return (
          <img style={{ width: "100%" }} src={embed.oembed.url as string} />
        );
      }
  }
};

// -- Content Post Serializer
// This function is used to change richText specifically for rich text components in content post
export const contentPostSerializer: JSXFunctionSerializer = function (
  type,
  element,
  content,
  children,
  key
) {
  switch (type) {
    case Element.heading1:
      let heading1Element = element as RTTextNodeBase;
      let attrTextH1 = textToHTMLAttribute(heading1Element.text || "");
      return (
        <h1 id={attrTextH1} key={key}>
          <HeadingAnchor tag={attrTextH1} />
          {children}
        </h1>
      );
    case Element.heading2:
      let heading2Element = element as RTTextNodeBase;
      let attrTextH2 = textToHTMLAttribute(heading2Element.text || "");
      return (
        <h2 id={attrTextH2} key={key}>
          <HeadingAnchor tag={attrTextH2} />
          {children}
        </h2>
      );
    case Element.heading3:
      let heading3Element = element as RTTextNodeBase;
      let attrTextH3 = textToHTMLAttribute(heading3Element.text || "");
      return (
        <h3 id={attrTextH3} key={key}>
          <HeadingAnchor tag={attrTextH3} />
          {children}
        </h3>
      );
    case Element.heading4:
      let heading4Element = element as RTTextNodeBase;
      let attrTextH4 = textToHTMLAttribute(heading4Element.text || "");
      return (
        <h4 id={attrTextH4} key={key}>
          <HeadingAnchor tag={attrTextH4} />
          {children}
        </h4>
      );
    case Element.heading5:
      let heading5Element = element as RTTextNodeBase;
      let attrTextH5 = textToHTMLAttribute(heading5Element.text || "");
      return (
        <h5 id={attrTextH5} key={key}>
          <HeadingAnchor tag={attrTextH5} />
          {children}
        </h5>
      );
    case Element.heading6:
      let heading6Element = element as RTTextNodeBase;
      let attrTextH6 = textToHTMLAttribute(heading6Element.text || "");
      return (
        <h6 id={attrTextH6} key={key}>
          <HeadingAnchor tag={attrTextH6} />
          {children}
        </h6>
      );
    default:
      return baseSerializer(type, element, content, children, key);
  }
};

// -- HTML Serializer
// This function will be used to change the way the HTML is loaded
export const htmlSerializer: JSXFunctionSerializer = function (
  type,
  element,
  content,
  children,
  key
) {
  return baseSerializer(type, element, content, children, key);
};
